import { makeWeapon, makeArmor, makePotion, makeCoins, makeResource, makeFood, makeTreasure, makeKey, makeCoinHoard } from "../items/ItemFactory.js";

/**
 * Weighted loot tables. rollLoot(name, floor) returns a single item object
 * (weapon / armor / potion / coins) or null ("nothing"). Enemies and each kind
 * of destructible reference a table by name.
 */
const TABLES = {
  enemy:  [["coins", 24], ["potion", 18], ["weapon", 19], ["armor", 17], ["resource", 10], ["food", 9], ["treasure", 6]],
  barrel: [["nothing", 26], ["coins", 24], ["potion", 19], ["resource", 12], ["food", 10], ["weapon", 4], ["armor", 4], ["treasure", 3]],
  crate:  [["nothing", 18], ["coins", 22], ["potion", 19], ["resource", 12], ["food", 10], ["weapon", 10], ["armor", 8], ["treasure", 5]],
  pot:    [["nothing", 36], ["coins", 30], ["potion", 16], ["food", 8], ["resource", 6], ["armor", 4], ["treasure", 4]],
  chest:  [["weapon", 32], ["armor", 30], ["potion", 16], ["food", 6], ["resource", 8], ["treasure", 16]],
};

/**
 * Locked-chest hauls, by tier. A locked chest costs Vault Keys to open (1/2/3
 * for bronze/silver/gold) and pays out a whole BUNDLE of items — richer than any
 * single smashable barrel/chest — with guaranteed gear whose rarity is floored
 * by tier. Returns an array of item objects. `mult` scales quantity for the
 * vault-room hoard (which pays even more, since its door already ate 3 keys).
 */
export function rollChest(tier = "bronze", floor = 1, mult = 1) {
  const out = [];
  const gear = (minIdx) => (Math.random() < 0.5 ? makeWeapon(floor, minIdx) : makeArmor(floor, minIdx));
  const rolls = (n) => { for (let i = 0; i < n; i++) { const it = rollLoot("chest", floor); if (it) out.push(it); } };

  if (tier === "gold") {
    out.push(makeCoins(floor + 6));
    out.push(gear(2));                                  // guaranteed Rare+ gear
    out.push(makeTreasure(floor + 3));
    rolls(Math.round(3 * mult));
    if (Math.random() < 0.35) out.push(makeKey(1));     // sometimes a key back
  } else if (tier === "silver") {
    out.push(makeCoins(floor + 3));
    out.push(gear(1));                                  // guaranteed Uncommon+ gear
    rolls(Math.round(2 * mult));
    if (Math.random() < 0.4) out.push(makeTreasure(floor + 1));
  } else {   // bronze
    out.push(makeCoins(floor + 1));
    rolls(Math.round(2 * mult));
  }
  return out.filter(Boolean);
}

// How many of a chest's 5 slots are filled, by tier (richer tiers fill more).
const CHEST_FILL = { bronze: [1, 3], silver: [2, 4], gold: [3, 5] };

/**
 * Contents of a LOCKED chest as a FIXED 5-slot array (item-or-null). The number
 * of filled slots scales with tier; slot contents are tier-flavoured, with a
 * guaranteed piece of gear (rarity floored by tier) so a chest is never a dud.
 * The player picks what to take from the chest screen; leftovers stay inside.
 */
export function rollChestSlots(tier = "bronze", floor = 1) {
  const [lo, hi] = CHEST_FILL[tier] || CHEST_FILL.bronze;
  const n = lo + Math.floor(Math.random() * (hi - lo + 1));
  const items = [];
  // richer chests bias their gold hoards bigger
  const hf = floor + (tier === "gold" ? 6 : tier === "silver" ? 3 : 0);
  // slot-0 guarantee by tier
  if (tier === "gold") items.push(Math.random() < 0.5 ? makeWeapon(floor, 2) : makeArmor(floor, 2));
  else if (tier === "silver") items.push(Math.random() < 0.5 ? makeWeapon(floor, 1) : makeArmor(floor, 1));
  else items.push(makeCoinHoard(hf));   // bronze always leads with a gold hoard
  // fill the rest — chests are a prime source of coin hoards
  while (items.length < n) {
    const r = Math.random();
    if (tier === "gold" && r < 0.12) items.push(makeKey(1));
    else if (r < 0.42) items.push(makeCoinHoard(hf));                                  // ~35–40% a gold hoard
    else if (r < 0.54) items.push(makeTreasure(floor + (tier === "gold" ? 3 : 1)));
    else items.push(rollLoot("chest", floor) || makeCoinHoard(hf));
  }
  // scatter the items into random distinct slots of a 5-wide tray
  const slots = new Array(5).fill(null);
  const idx = [0, 1, 2, 3, 4];
  for (let i = idx.length - 1; i > 0; i--) { const j = (Math.random() * (i + 1)) | 0; [idx[i], idx[j]] = [idx[j], idx[i]]; }
  for (let i = 0; i < items.length && i < 5; i++) slots[idx[i]] = items[i];
  return slots;
}

export function rollLoot(table, floor = 1) {
  const t = TABLES[table] || TABLES.enemy;
  const total = t.reduce((s, e) => s + e[1], 0);
  let n = Math.random() * total;
  let kind = "nothing";
  for (const [k, w] of t) { if (n < w) { kind = k; break; } n -= w; }
  switch (kind) {
    case "coins": return makeCoins(floor);
    case "potion": return makePotion(floor);
    case "weapon": return makeWeapon(floor);
    case "armor": return makeArmor(floor);
    case "resource": return makeResource(floor);
    case "food": return makeFood();
    case "treasure": return makeTreasure(floor);
    default: return null;
  }
}
